import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import Input from '../components/ui/Input';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { getFriendlyError } from '../utils/friendlyError';
import { getDashboardPath } from '../utils/rolePaths';

const RequiredPasswordChangePage = () => {
  const { t } = useTranslation();
  const { user, refreshUser } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (newPassword.length < 8) {
      showToast(t('forms.minLength', { min: 8 }), 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast(t('forms.passwordMismatch'), 'error');
      return;
    }

    setSubmitting(true);
    try {
      await api.put('/auth/change-password', { currentPassword, newPassword });
      const refreshed = await refreshUser();
      showToast(t('auth.passwordChanged'), 'success');
      navigate(getDashboardPath(refreshed.role, refreshed), { replace: true });
    } catch (err) {
      showToast(getFriendlyError(err, t('profile.passwordChangeFailed')), 'error');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-50 dark:bg-zinc-950 px-4">
      <div className="w-full max-w-md rounded-2xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-8 shadow-xl">
        <h1 className="text-xl font-semibold">{t('auth.requiredPasswordChangeTitle')}</h1>
        <p className="mt-2 text-sm text-zinc-600 dark:text-zinc-400">
          {t('auth.requiredPasswordChangeSubtitle', { name: user?.firstName })}
        </p>

        <form onSubmit={onSubmit} className="mt-6 space-y-4">
          <Input
            label={t('profile.currentPassword')}
            id="currentPassword"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
          <Input
            label={t('profile.newPassword')}
            id="newPassword"
            type="password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            minLength={8}
          />
          <Input
            label={t('profile.confirmNewPassword')}
            id="confirmPassword"
            type="password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
          />
          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-lg bg-glow-600 py-2.5 text-sm font-medium text-white hover:bg-glow-500 disabled:opacity-50"
          >
            {submitting ? t('profile.updating') : t('auth.setNewPassword')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default RequiredPasswordChangePage;
